import React from "react"
import { useTranslation } from "react-i18next"

import { ProductInformation } from "shared/types/__generated__/graphql"
import { DataTableView } from "shared/ui/data-table/DataTableView"
import { Label } from "shared/ui/label/Label"
import { PropertyList } from "shared/ui/property-list/PropertyList"
import { Skeleton } from "shared/ui/skeleton/Skeleton"

import "./styles/_product-external-dimensioning.scss"

interface ProductExternalDimensioningProps {
  productInformation: ProductInformation | null | undefined
  isLoading?: boolean
  hideEnvironmentalAttributes?: boolean
}

export const ProductExternalDimensioning = React.memo(
  ({
    productInformation,
    isLoading,
    hideEnvironmentalAttributes = false
  }: ProductExternalDimensioningProps) => {
    const { t } = useTranslation()

    const dimensioning = productInformation?.dimensioning

    if (!dimensioning && !isLoading) {
      return null
    }

    return (
      <div className="product-external-dimensioning">
        {isLoading ? (
          <Skeleton count={4} />
        ) : (
          <>
            {!hideEnvironmentalAttributes &&
            dimensioning?.environmentalAttributes?.length ? (
              <div className="product-external-dimensioning__block">
                <Label
                  variant="bold"
                  className="product-external-dimensioning__header"
                >
                  {t("Product.Configuration.VariantKeyRelatedParams")}
                </Label>
                <PropertyList
                  hideChip
                  className="product-external-dimensioning__content"
                  list={dimensioning.environmentalAttributes.map(a => ({
                    label: a.label,
                    value: a.value
                  }))}
                />
              </div>
            ) : null}
            {dimensioning?.physicalAttributes?.length ? (
              <div className="product-external-dimensioning__block">
                <Label
                  variant="bold"
                  className="product-external-dimensioning__header"
                >
                  {t("Product.Configuration.PhysicalParams")}
                </Label>
                <PropertyList
                  hideChip
                  className="product-external-dimensioning__content"
                  list={dimensioning.physicalAttributes.map(a => ({
                    label: a.label,
                    value: `${a.value}${a.unit ? ` ${a.unit}` : ""}`
                  }))}
                />
              </div>
            ) : null}
            {dimensioning?.supplementaryText ? (
              <div className="product-external-dimensioning__block">
                <span className="label">{dimensioning.supplementaryText}</span>
              </div>
            ) : null}
            {dimensioning?.physicalTables?.length ? (
              <div className="product-external-dimensioning__tables product-external-dimensioning__block">
                {dimensioning.physicalTables.map((table, index) => (
                  <DataTableView
                    key={table.description ?? index}
                    id={`product-external-dimensioning-table-${index}`}
                    data={table}
                    label={table.description ?? ""}
                    hideLabel={false}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
      </div>
    )
  }
)
